import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert as ChakraAlert,
  AlertIcon as ChakraAlertIcon,
  Text as ChakraText,
} from '@chakra-ui/react'

import { Alert } from '@hivewatch/dark'

import {
  DISPATCH_NO_AVAILABLE_GUARDS_ALERT_SHOWN,
  trackEvent,
} from '@/utils/analytics'

import { MixpanelDataIProps } from '../../shared/types/types'

type ColorScheme = 'dark' | 'light'

interface NoAvailableGuardsAlertIProps {
  hasGuards: boolean
  isV1: boolean
  /** Only applies to the dark-package alert; the v1 alert is light-only. */
  colorScheme?: ColorScheme
  mixpanelData?: MixpanelDataIProps
}

/**
 * Warns that there is nobody to dispatch. Renders the Chakra v1 alert for the
 * pre-rebrand dispatch panel and the dark-package alert for the rebranded one,
 * so both flows share a single gate and a single string.
 */
export const NoAvailableGuardsAlert = ({
  hasGuards,
  isV1,
  colorScheme = 'dark',
  mixpanelData,
}: NoAvailableGuardsAlertIProps) => {
  const { t } = useTranslation()
  // Guards are polled, so hasGuards can flip and re-run the effect;
  // the ref caps the event at once per mount.
  const hasTrackedShownRef = useRef(false)

  useEffect(() => {
    if (!hasGuards && !hasTrackedShownRef.current) {
      hasTrackedShownRef.current = true
      trackEvent(DISPATCH_NO_AVAILABLE_GUARDS_ALERT_SHOWN, mixpanelData)
    }
  }, [hasGuards, mixpanelData])

  if (hasGuards) {
    return null
  }

  const message = t('incidents:dispatch_no_available_guards')

  if (isV1) {
    return (
      <ChakraAlert borderRadius='10px' mb='24px' mt='16px' status='warning'>
        <ChakraAlertIcon />
        <ChakraText fontSize='14px' fontWeight={400}>
          {message}
        </ChakraText>
      </ChakraAlert>
    )
  }

  return <Alert colorScheme={colorScheme} title={message} variant='warning' />
}
